import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden bg-[#0B0F1A]"
    >
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(247,245,240,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(247,245,240,0.025) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Gradient blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-0 left-0 w-[700px] h-[700px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(26,58,92,0.18) 0%, transparent 65%)' }}
        />
        <div
          className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(196,85,58,0.10) 0%, transparent 65%)' }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-16">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-8">

          {/* Left — text */}
          <div
            className={`flex-1 max-w-xl transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="font-['JetBrains_Mono'] text-[10px] tracking-[0.22em] uppercase text-[#D4A853] mb-10 opacity-90">
              {t('hero_label')}
            </p>

            <h1 className="font-['Instrument_Serif'] text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] tracking-tight mb-6">
              {t('hero_headline_1')}<br />
              <em className="not-italic text-[#D4A853] italic">{t('hero_headline_2')}</em>
            </h1>

            <p className="font-['DM_Sans'] text-[#6B7280] text-base md:text-lg leading-relaxed mb-12 max-w-md">
              {t('hero_sub')}
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4">
              <button
                onClick={() => scrollTo('contacto')}
                className="font-['DM_Sans'] text-sm font-medium bg-[#C4553A] text-white px-7 py-3.5 rounded-md hover:bg-[#b04030] transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                {t('hero_cta_primary')}
              </button>
              <button
                onClick={() => scrollTo('portfolio')}
                className="font-['DM_Sans'] text-sm font-medium text-[#F7F5F0] px-7 py-3.5 rounded-md border border-[rgba(247,245,240,0.18)] hover:border-[rgba(247,245,240,0.38)] transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                {t('hero_cta_secondary')}
              </button>
            </div>
          </div>

          {/* Right — logo */}
          <div
            className={`flex-shrink-0 flex items-center justify-center transition-all duration-1000 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <img
              src="https://storage.readdy-site.link/project_files/b2da5f54-bd0d-457f-a912-2ea2818e9859/bada050b-601d-483d-96e9-d2895b45fc69_logo-horizontal-dark-bg.svg?v=cfa5b88bb85d44751afeed3885547ffa"
              alt="Alice & Bob Capital"
              className="w-[420px] md:w-[570px] lg:w-[660px] h-auto opacity-90"
            />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-20">
        <div className="w-px h-10 bg-[#F7F5F0]" />
      </div>
    </section>
  );
}
